import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Skeleton } from 'gd-design-library';
import { MessageResponse } from '@ujet/websdk-headless';
import { ChatbotWidgetContext } from 'components/ChatbotWidget/utils/ChatbotWidgetContext';
import { View } from 'components/ChatbotWidget/constants/view';
import { ChatHeader } from 'components/ChatbotWidget/components/ChatHeader';
import { ChatInputs } from 'components/ChatbotWidget/components/ChatInputs';
import { TypingIndicator } from 'components/ChatbotWidget/components/TypingIndicator';
import { Message } from 'components/ChatbotWidget/components/Message';
import { useRouter } from 'next/router';
import styles from './Chat.module.scss';
import classNames from 'classnames/bind';
import {
  previousConversationPrefix,
  redirectUserToCheckoutMessage
} from 'components/ChatbotWidget/constants/chat';

const cx = classNames.bind(styles);
const messagesContainerClass = 'chat-messages-container';
const loadingSkeletonsClass = 'chat-loading-skeletons';

export const Chat = (): React.ReactElement => {
  const { setView, client } = useContext(ChatbotWidgetContext);
  const [ isChatLoading, setIsChatLoading ] = useState(true);
  const [ whoIsTyping, setWhoIsTyping ] = useState<{ userType: string, id: number } | null>(null);
  const [ messages, setMessages ] = useState<MessageResponse[]>([]);

  const router = useRouter();

  const messagesContainer = useRef<HTMLDivElement>(null);

  const handleConnected = useCallback(() => {
    if (!client || !client.chat.connected) return;

    client.fetchMessages().then(newMessages => {
      setMessages(newMessages);
      setIsChatLoading(false);
    });
  }, [ client ]);

  const handleTypingStarted = useCallback((userId: string) => {
    const [ userType, id ] = userId.split('-');
    setWhoIsTyping({ userType, id: parseInt(id) });
  }, []);

  const handleTypingEnded = useCallback(() => { setWhoIsTyping(null); }, []);

  const handleMessage = useCallback(
    (message: MessageResponse) => {
      if (message?.content === redirectUserToCheckoutMessage) {
        router.push('/checkout');

        return;
      }

      if (typeof message?.content === 'string' && message?.content?.startsWith(previousConversationPrefix)) {
        return;
      }

      setMessages(messages => [ ...messages, message ]);
    },
    [ router ]
  );

  /**
   * Provides context for AI Coach when a new member joins the conversation
   */
  const handleMemberJoined = useCallback(() => {
    const conversationHistory = messages
      .filter(message => message.type === 'text')
      .map(
        message => `[${message.$userType === 'end_user' ? 'Customer' : 'Virtual Agent'}]: ${message.content}`
      ).join('\n');

    client.sendTextMessage(`${previousConversationPrefix}\n${conversationHistory}`);
  }, [ client, messages, previousConversationPrefix ]);

  const handleChatEnded = useCallback(() => {
    setView(View.RATING);
  }, [ setView ]);

  useEffect(() => {
    if (!client) return;

    client.on('chat.connected', handleConnected);
    client.on('chat.typingStarted', handleTypingStarted);
    client.on('chat.typingEnded', handleTypingEnded);
    client.on('chat.message', handleMessage);
    client.on('chat.memberJoined', handleMemberJoined);
    client.on('chat.ended', handleChatEnded);

    return (): void => {
      client.off('chat.connected', handleConnected);
      client.off('chat.typingStarted', handleTypingStarted);
      client.off('chat.typingEnded', handleTypingEnded);
      client.off('chat.message', handleMessage);
      client.off('chat.memberJoined', handleMemberJoined);
      client.off('chat.ended', handleChatEnded);
    };
  }, [ client,
    handleConnected,
    handleTypingStarted,
    handleTypingEnded,
    handleMessage,
    handleMemberJoined,
    handleChatEnded ]);

  const scrollToBottom = useCallback(() => {
    messagesContainer.current?.scrollTo({
      top: messagesContainer.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [ messagesContainer ]);

  useEffect(scrollToBottom, [ scrollToBottom, whoIsTyping, messages ]);

  useEffect(function setTypingIndicatorForVirtualAgent() {
    const lastMessage = messages[messages.length - 1];
    const previousMessage = messages[messages.length - 2];

    const isWaitingForVirtualAgent = lastMessage?.$userType === 'end_user' && previousMessage?.$userType === 'virtual_agent';

    if (isWaitingForVirtualAgent && whoIsTyping === null) {
      setWhoIsTyping({
        userType: previousMessage?.$userType,
        id: previousMessage?.$userId
      });
    } else if (!isWaitingForVirtualAgent && whoIsTyping?.userType === 'virtual_agent') {
      setWhoIsTyping(null);
    }
  }, [ whoIsTyping, messages ]);

  return (
    <>
      <ChatHeader />
      <div
        className={ cx(messagesContainerClass) }
        ref={ messagesContainer }
      >
        {isChatLoading && (
          <div className={ cx(loadingSkeletonsClass) }>
            <Skeleton variant='circular' width='40px' height='40px' />
            <Skeleton width='360px' height='115px' />
          </div>
        )}
        {messages.map((message, index) => (
          <Message
            key={ index }
            message={ message }
            index={ index }
            messagesLength={ messages.length }
            scrollToBottom={ scrollToBottom }
          />
        ))}
        <TypingIndicator whoIsTyping={ whoIsTyping } />
      </div>
      <ChatInputs isFirstUserMessage={ messages.length === 1 } disabled={ isChatLoading } />
    </>
  );
};
